/**
 * Provider Data Utilities
 * Global + Local Hybrid Implementation - Data Filtering Helpers
 * 
 * Utility functions to support hierarchical provider filtering where global provider
 * selection acts as primary filter and local filters work within that scope
 */

import type { CloudProvider } from '@/types/cloudProvider';

/**
 * Generic interface for resources that have provider information
 */
export interface ProviderResource {
  provider: CloudProvider;
  [key: string]: any;
}

/**
 * Hierarchical filter function for Global + Local Hybrid approach
 * 
 * @param resources - Array of resources with provider property
 * @param globalProvider - Currently selected global provider
 * @param localProviderFilter - Local page filter ("all" means show all within global provider)
 * @returns Filtered resources following the hierarchy
 */
export function filterByProviderHierarchy<T extends ProviderResource>(
  resources: T[],
  globalProvider: CloudProvider,
  localProviderFilter: string = "all"
): T[] {
  // Step 1: Filter by global provider (primary filter)
  const globalFiltered = resources.filter(resource => resource.provider === globalProvider);
  
  // Step 2: Apply local filter within global provider scope (secondary filter)
  if (localProviderFilter === "all" || localProviderFilter === globalProvider) {
    return globalFiltered;
  }
  
  // If local filter specifies a different provider, return empty (invalid in hybrid model)
  return [];
}

/**
 * Get available local provider filter options based on global provider
 * 
 * @param globalProvider - Currently selected global provider
 * @returns Available filter options for local filtering
 */
export function getLocalProviderFilterOptions(globalProvider: CloudProvider) {
  return [
    { value: "all", label: `All ${globalProvider.toUpperCase()} Resources` },
    { value: globalProvider, label: `${globalProvider.toUpperCase()} Only` }
  ];
}

/**
 * Generate filter summary for UI display
 * 
 * @param globalProvider - Currently selected global provider
 * @param localProviderFilter - Current local filter setting
 * @returns Human-readable filter description
 */
export function getFilterSummary(globalProvider: CloudProvider, localProviderFilter: string): string {
  const providerName = globalProvider.toUpperCase();
  
  if (localProviderFilter === "all") {
    return `Showing all ${providerName} resources`;
  }
  
  if (localProviderFilter === globalProvider) {
    return `Showing ${providerName} resources`;
  }
  
  return `Showing ${providerName} resources`;
}

/**
 * Count resources by provider for global provider context
 * 
 * @param resources - Array of resources with provider property
 * @param globalProvider - Currently selected global provider
 * @returns Resource count for the global provider
 */
export function countResourcesForProvider<T extends ProviderResource>(
  resources: T[],
  globalProvider: CloudProvider
): number {
  return resources.filter(resource => resource.provider === globalProvider).length;
}

/**
 * Validate if a local provider filter is valid within global context
 * 
 * @param globalProvider - Currently selected global provider
 * @param localProviderFilter - Local filter to validate
 * @returns Whether the local filter is valid in the global context
 */
export function isValidLocalProviderFilter(
  globalProvider: CloudProvider,
  localProviderFilter: string
): boolean {
  return localProviderFilter === "all" || localProviderFilter === globalProvider;
}

/**
 * Migration helper: Convert old "all providers" filter to hybrid model
 * 
 * @param oldProviderFilter - Previous provider filter value
 * @param globalProvider - Current global provider
 * @returns Equivalent local filter in hybrid model
 */
export function migrateProviderFilter(
  oldProviderFilter: string,
  globalProvider: CloudProvider
): string {
  // If old filter was "all", convert to "all within global provider"
  if (oldProviderFilter === "all") {
    return "all";
  }
  
  // If old filter matches global provider, keep it
  if (oldProviderFilter === globalProvider) {
    return globalProvider;
  }
  
  // If old filter was different provider, reset to "all within global provider"
  return "all";
}